import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';

import { NgbCarouselConfig, NgbCarouselModule } from '@ng-bootstrap/ng-bootstrap';
import { first } from 'rxjs';
import { ReCaptchaV3Service } from 'ng-recaptcha';

import { DiscoverService } from '../../shared/services/discover.service';
import { AnalyticsService } from 'app/shared/services/analytics.service';
import { AuthService } from 'app/auth/auth.service';
import { NavigationIntentService } from 'app/shared/services/navigation-intent.service';
import { ChatStateService } from '../../shared/services/chat-state.service';
import { StatsigService } from 'app/shared/services/statsig.service';

import { ButtonComponent } from '../../shared/components/button/button.component';
import { DiscoverItemComponent } from '../../components/discover-item/discover-item.component';
import { DiscoverFiltersComponent } from '../../components/discover-filters/discover-filters.component';

import { IButtonConfig } from '../../shared/interfaces/button.interfaces';
import {
  IDiscoverItem,
  IDiscoverResponse,
  INationalityFilter,
  IDiscoverFilter
} from '../../interfaces/discover.interfaces';
import { IAudioState } from '../../interfaces/creator.interfaces';

import { EButtonActionTypes, EButtonSizes, EButtonTypes } from '../../shared/enums/button.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';
import { EDiscoverFilter } from 'app/enums/discover.enums';
import { ELocalStorageKey } from '../../shared/enums/shared.enums';

import { COUNTRIES, ICountry } from 'app/shared/const/countries';

@Component({
  selector: 'stxt-home-page',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    NgbCarouselModule,
    ButtonComponent,
    DiscoverItemComponent,
    DiscoverFiltersComponent
  ],
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.scss',
  providers: [NgbCarouselConfig, ReCaptchaV3Service]
})
export class HomePageComponent implements OnInit {
  public isLander: boolean = false;
  public discoverData: IDiscoverItem[];
  public filteredDiscoverData: IDiscoverItem[] = [];
  public userId: string = localStorage.getItem(ELocalStorageKey.UserId);
  public isFiltersEnabled: boolean;
  public chatNavButton: IButtonConfig = {
    text: 'Chat now',
    fill: EButtonTypes.SecondaryFilled,
    buttonSize: EButtonSizes.Default
  };
  public selectedPreviewAudio: string;
  public isBannerEnabled: boolean;
  protected showIndicators: boolean;
  public creatorInfo: { creator_name: string; thumbnail_image: string };
  public user = JSON.parse(localStorage.getItem(ELocalStorageKey.User));

  private isRecaptchaEnabled: boolean;

  private appliedFilters: boolean = false;
  private readonly countries: ICountry[] = COUNTRIES;
  public nationalityFilters: INationalityFilter[] = [];
  private previousNationalityFilters: INationalityFilter[] = [];
  private currentActiveFilters: string[] = [];
  public availableFilters: IDiscoverFilter[] = [];

  private readonly filterResultsCache: Map<string, IDiscoverItem[]> = new Map();
  private readonly staticFilters: IDiscoverFilter[] = [
    {
      name: '🎁 For you',
      value: EDiscoverFilter.ForYou
    },
    {
      name: '☺️ SFW',
      value: EDiscoverFilter.SFW
    },
    {
      name: '🔥️ NSFW',
      value: EDiscoverFilter.NSFW
    },
    {
      name: '🦋 Tattoos',
      value: EDiscoverFilter.Tattoos
    },
    {
      name: '💠 Piercings',
      value: EDiscoverFilter.Piercings
    },
    {
      name: '💛 Blondes',
      value: EDiscoverFilter.Blondes
    },
    {
      name: '️🤎 Brunettes',
      value: EDiscoverFilter.Brunettes
    },
    {
      name: '🌎 Nationality',
      value: '',
      isMultiple: true
    }
  ];

  constructor(
    private discoverService: DiscoverService,
    public router: Router,
    private analyticsService: AnalyticsService,
    public authService: AuthService,
    private config: NgbCarouselConfig,
    private reCaptchaV3Service: ReCaptchaV3Service,
    private readonly navigationIntentService: NavigationIntentService,
    private readonly chatStateService: ChatStateService,
    private readonly statsigService: StatsigService
  ) {
    config.wrap = true;
    config.interval = 3000;
    config.keyboard = false;
    config.pauseOnHover = true;
    config.animation = true;
  }

  ngOnInit(): void {
    this.fetchDiscoverData();
    this.checkStatsigFeatureGate();
  }

  fetchDiscoverData(): void {
    this.discoverService
      .getDiscoverData()
      .pipe(first())
      .subscribe({
        next: (response: IDiscoverResponse) => {
          this.discoverData = this.sortDiscoverData(
            response.creators.map(item => ({
              ...item,
              isLoaded: false,
              hasError: false,
              isNew: this.isNewItem(item)
            }))
          );
          this.nationalityFilters = this.getNationalityFilters(response.creators);
          this.showIndicators = this.discoverData.filter(item => item?.featured).length > 1;
          this.discoverService.creators$.next(response.creators);

          this.availableFilters = this.prepareFilters(response.creators);
        }
      });
  }

  sortDiscoverData(creators: IDiscoverItem[]): IDiscoverItem[] {
    this.filteredDiscoverData = creators.sort((a, b) => {
      const aReady = a.ready === undefined ? false : a.ready;
      const bReady = b.ready === undefined ? false : b.ready;
      return aReady === bReady ? 0 : aReady ? -1 : 1;
    });
    return this.filteredDiscoverData;
  }

  getNationalityFilters(creators: IDiscoverItem[]): INationalityFilter[] {
    const uniqueCreatorNationalities = [
      ...new Set(creators.map(item => item.nationality.toLowerCase()))
    ];

    return uniqueCreatorNationalities
      .map(nationality =>
        this.countries.find(country => country.nationality.toLowerCase() === nationality)
      )
      .filter(Boolean);
  }

  async checkStatsigFeatureGate(): Promise<void> {
    try {
      await this.statsigService.initializeStatsig(this.userId);

      this.isRecaptchaEnabled = await this.statsigService.checkFeatureGate(EFeatureGates.RECAPTCHA);
      this.isFiltersEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.DISCOVER_FILTER
      );
      this.isBannerEnabled = await this.statsigService.checkFeatureGate(
        EFeatureGates.HOMEPAGE_BANNER
      );
    } catch (error) {
      this.isRecaptchaEnabled = false;
      this.isBannerEnabled = false;
      this.isFiltersEnabled = false;
    }
  }

  async navigateToChat(clickText: string, creator: IDiscoverItem): Promise<void> {
    if (this.isRecaptchaEnabled) {
      this.reCaptchaV3Service
        .execute('chat_now_action')
        .pipe(first())
        .subscribe({
          next: async () => {
            await this.processChatNavigation(clickText, creator);
          }
        });
    } else {
      await this.processChatNavigation(clickText, creator);
    }
  }

  filterCards(filter: string | string[] | INationalityFilter[]): void {
    if (!Array.isArray(filter)) {
      this.filterBySingleParam(filter);
      return;
    }
    if (filter.length === 0) {
      this.filterBySingleParam(filter as string[]);
      return;
    }
    if (this.isNationalityFilterArray(filter)) {
      this.filterNationality(filter as INationalityFilter[]);
      return;
    }
    this.filterBySingleParam(filter as string[]);
  }

  private isNationalityFilterArray(filter: string[] | INationalityFilter[]): boolean {
    return filter.length > 0 && typeof filter[0] === 'object';
  }

  filterNationality(filters: INationalityFilter[]): void {
    if (filters.length === 0) {
      this.filteredDiscoverData = this.discoverData;
      this.previousNationalityFilters = [];
      return;
    }

    const isAdding = filters.length > this.previousNationalityFilters.length;

    if (isAdding) {
      const lastAddedFilter = filters[filters.length - 1];
      const nationalities = [lastAddedFilter.nationality];
      this.fetchFilteredData({ nationality: nationalities });
    } else {
      this.previousNationalityFilters = [...filters];
      this.reapplyCurrentFilters();
      return;
    }

    this.previousNationalityFilters = [...filters];
  }

  filterBySingleParam(filter: string | string[]): void {
    if (filter === EDiscoverFilter.ForYou) {
      this.filteredDiscoverData = this.discoverData;
      this.appliedFilters = false;
      this.currentActiveFilters = [];
      return;
    }

    if (Array.isArray(filter)) {
      this.handleArrayFilters(filter);
      return;
    }

    this.handleSingleFilterToggle(filter);
  }

  private handleArrayFilters(filter: string[]): void {
    const previousFilters = [...this.currentActiveFilters];
    this.currentActiveFilters = [...filter];

    if (filter.length === 0 || (filter.length === 1 && filter[0] === EDiscoverFilter.ForYou)) {
      this.filteredDiscoverData = this.discoverData;
      this.appliedFilters = false;
      return;
    }

    const addedFilters = filter.filter(f => !previousFilters.includes(f));
    const removedFilters = previousFilters.filter(
      f => !filter.includes(f) && f !== EDiscoverFilter.ForYou
    );

    if (addedFilters.length === 0 && removedFilters.length > 0) {
      this.rebuildFiltersFromScratch(filter);
      return;
    }

    if (addedFilters.length > 0) {
      this.applyAddedFilters(addedFilters, previousFilters);
    }
  }

  private handleSingleFilterToggle(filter: string): void {
    const filterIndex = this.currentActiveFilters.indexOf(filter);

    if (filterIndex > -1) {
      this.currentActiveFilters.splice(filterIndex, 1);
      this.reapplyCurrentFilters();
      return;
    }

    this.currentActiveFilters = this.currentActiveFilters.filter(f => f !== EDiscoverFilter.ForYou);
    this.currentActiveFilters.push(filter);
    this.getRequestFilterBody(filter);
  }

  private applyAddedFilters(addedFilters: string[], previousFilters: string[]): void {
    const isStartingFresh =
      previousFilters.length === 0 || previousFilters[0] === EDiscoverFilter.ForYou;

    if (isStartingFresh) {
      this.appliedFilters = false;
    }

    for (const [index, addedFilter] of addedFilters.entries()) {
      const isFirstFilterOfNewSet = index === 0 && previousFilters.length === 0;
      if (isFirstFilterOfNewSet) {
        this.appliedFilters = false;
      }
      this.getRequestFilterBody(addedFilter);
    }
  }

  private rebuildFiltersFromScratch(filters: string[]): void {
    this.filteredDiscoverData = [];
    this.appliedFilters = false;

    const allCreators: IDiscoverItem[] = [];

    for (const filter of filters) {
      const requestBody = this.buildFilterBody(filter, false);
      const cacheKey = JSON.stringify(requestBody);

      const cachedCreators = this.filterResultsCache.get(cacheKey);
      if (cachedCreators) {
        allCreators.push(...cachedCreators);
      } else {
        this.getRequestFilterBody(filter);
      }
    }

    if (allCreators.length > 0) {
      this.filteredDiscoverData = this.sortDiscoverData(this.removeDuplicateCreators(allCreators));
      this.appliedFilters = true;
    }
  }

  getRequestFilterBody(filter: string): void {
    const requestBody = this.buildFilterBody(filter, false);
    const cacheKey = JSON.stringify(requestBody);

    const cachedCreators = this.filterResultsCache.get(cacheKey);
    if (cachedCreators) {
      this.mergeCreatorsToFiltered(cachedCreators);
      return;
    }
    this.fetchFilteredData(requestBody, cacheKey);
  }

  fetchFilteredData(
    requestBody: { [key: string]: boolean | string | string[] },
    cacheKey?: string
  ): void {
    this.discoverService
      .getDiscoverData(requestBody)
      .pipe(first())
      .subscribe({
        next: (response: IDiscoverResponse) => {
          const newCreators = response.creators || [];

          if (cacheKey) {
            this.filterResultsCache.set(cacheKey, newCreators);
          }

          this.mergeCreatorsToFiltered(newCreators);
        }
      });
  }

  private mergeCreatorsToFiltered(newCreators: IDiscoverItem[]): void {
    const existingData = this.filteredDiscoverData || [];

    const filteredData = this.appliedFilters
      ? this.removeDuplicateCreators([...existingData, ...newCreators])
      : newCreators;

    this.filteredDiscoverData = this.sortDiscoverData(filteredData);
    this.appliedFilters = true;
  }

  getHairColorFilter(filter: string): string {
    switch (filter) {
      case EDiscoverFilter.Blondes:
        return 'blond';
      case EDiscoverFilter.Brunettes:
        return 'brunette';
      default:
        return filter;
    }
  }

  private removeDuplicateCreators(creators: IDiscoverItem[]): IDiscoverItem[] {
    const seen = new Set<string>();
    return creators.filter(creator => {
      if (seen.has(creator.id)) {
        return false;
      }
      seen.add(creator.id);
      return true;
    });
  }

  private reapplyCurrentFilters(): void {
    if (this.currentActiveFilters.length === 0 && !this.previousNationalityFilters.length) {
      this.filteredDiscoverData = this.discoverData;
      this.appliedFilters = false;
      return;
    }

    this.filteredDiscoverData = this.filteredDiscoverData.filter(item => {
      const allFilters = [
        ...this.currentActiveFilters,
        ...this.previousNationalityFilters
          .filter(n => n.nationality)
          .map(n => ({ nationality: n.nationality }))
      ];
      return allFilters.every(filter => {
        const filterBody = this.buildFilterBody(filter, true);
        return this.matchesFilter(item, filterBody);
      });
    });
  }

  private matchesFilter(
    item: IDiscoverItem,
    filterBody: { [key: string]: boolean | string | string[] }
  ): boolean {
    return Object.entries(filterBody).every(([key, value]) => {
      const itemValue = item[key as keyof IDiscoverItem];

      if (Array.isArray(value)) {
        const stringValue =
          itemValue !== null && itemValue !== undefined && typeof itemValue !== 'object'
            ? String(itemValue)
            : '';
        return value.includes(stringValue);
      }

      return itemValue === value;
    });
  }

  private buildFilterBody(
    filter: string | { nationality: string },
    isLocal: boolean = false
  ): { [key: string]: boolean | string } | null {
    if (!filter) return null;

    if (typeof filter === 'object' && 'nationality' in filter) {
      return { nationality: filter.nationality };
    }

    switch (filter) {
      case EDiscoverFilter.SFW:
      case EDiscoverFilter.NSFW:
        return isLocal
          ? { not_safe_for_work: filter === EDiscoverFilter.NSFW }
          : { rating: filter };

      case EDiscoverFilter.Blondes:
      case EDiscoverFilter.Brunettes:
        return { hair: this.getHairColorFilter(filter) };

      case EDiscoverFilter.ComingSoon:
        return { ready: false };

      default:
        return typeof filter === 'string' && filter.trim() !== '' ? { [filter]: true } : null;
    }
  }

  trackSelectedVoice(audioState: IAudioState): void {
    if (audioState.isPlaying) {
      this.selectedPreviewAudio = audioState.id as string;
    } else if (this.selectedPreviewAudio === audioState.id) {
      this.selectedPreviewAudio = null;
    }
  }

  onImageStateChange(event: Event, discoverItem: IDiscoverItem): void {
    const isLoad = event.type === 'load';
    discoverItem.isLoaded = isLoad;
    discoverItem.hasError = !isLoad;
  }

  private async processChatNavigation(clickText: string, creator: IDiscoverItem): Promise<void> {
    this.creatorInfo = { creator_name: creator.name, thumbnail_image: creator.image };
    await this.authService.checkUserCredentials();
    localStorage.setItem(ELocalStorageKey.BackUrl, '/');
    localStorage.setItem(ELocalStorageKey.CreatorInfo, JSON.stringify(this.creatorInfo));
    this.userId = localStorage.getItem(ELocalStorageKey.UserId);

    const pageName = 'Home';
    this.analyticsService.click(
      clickText,
      `/chat/${creator.id}`,
      pageName,
      EButtonActionTypes.Navigation,
      `/chat/${creator.id}`
    );
    this.navigationIntentService.setIntent(`/chat/${creator.id}`);
    this.chatStateService.setPendingCreatorId(creator.id);
    this.router.navigateByUrl(`/chat/${creator.id}`);
  }

  private prepareFilters(creators: IDiscoverItem[]): IDiscoverFilter[] {
    return [
      this.staticFilters[0],
      ...(creators.some(creator => this.isNewItem(creator))
        ? [{ name: '✨ New', value: EDiscoverFilter.New }]
        : []),
      ...(creators.some(creator => creator.ready === false)
        ? [{ name: '🚀 Coming soon', value: EDiscoverFilter.ComingSoon }]
        : []),
      ...this.staticFilters.slice(1)
    ];
  }

  private isNewItem(item: IDiscoverItem): boolean {
    if (!item.published_at) return false;
    const publishedDate = new Date(item.published_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return publishedDate >= thirtyDaysAgo;
  }
}
